import { useState, useEffect, useRef } from 'react';

interface TextToSpeechOptions {
  rate?: number;
  volume?: number;
  pitch?: number;
  lang?: string;
}


const getRoleLabel = (el: Element): string | null => {
  const tag = el.tagName.toLowerCase();
  const role = el.getAttribute('role');

  if (tag === 'button' || role === 'button') return 'buton';
  if (tag === 'a' && (el as HTMLAnchorElement).href) return 'bağlantı';
  if (role === 'link') return 'bağlantı';
  if (tag === 'input') {
    const type = ((el as HTMLInputElement).type || 'text').toLowerCase();
    if (type === 'checkbox') return 'onay kutusu';
    if (type === 'radio') return 'seçenek';
    if (type === 'password') return 'şifre alanı';
    if (type === 'email') return 'e-posta alanı';
    if (type === 'tel') return 'telefon alanı';
    if (type === 'submit' || type === 'button') return 'buton';
    return 'giriş alanı';
  }
  if (tag === 'textarea') return 'metin alanı';
  if (tag === 'select') return 'seçim listesi';
  return null;
};


const getStateLabel = (el: Element): string => {
  const parts: string[] = [];
  if ((el as HTMLInputElement).disabled || el.getAttribute('aria-disabled') === 'true') {
    parts.push('devre dışı');
  }
  const pressed = el.getAttribute('aria-pressed');
  if (pressed === 'true') parts.push('basılı');
  else if (pressed === 'false') parts.push('basılı değil');
  const expanded = el.getAttribute('aria-expanded');
  if (expanded === 'true') parts.push('açık');
  else if (expanded === 'false') parts.push('kapalı');
  const checked = el.getAttribute('aria-checked') ?? (el as HTMLInputElement).checked?.toString();
  if (checked === 'true') parts.push('işaretli');
  else if (checked === 'false') parts.push('işaretsiz');
  return parts.join(', ');
};

const getElementName = (el: Element): string => {
  const ariaLabel = el.getAttribute('aria-label');
  if (ariaLabel?.trim()) return ariaLabel.trim();
  return (el.textContent || '').replace(/\s+/g, ' ').trim();
};

// Okumadan tamamen dışlanacak alanlar (footer chrome, erişilebilirlik widget'ı, script/style)
const EXCLUDE_SELECTOR =
  '.accessibility-widget, .floating-accessibility-button, [data-accessibility-widget="true"], footer, #dashboardFooter, script, style';

// Ekranda görünmeyen elementler okunmamalı.
// Responsivede gizlenen menüleri atlar.
const isElementHidden = (el: Element): boolean => {
  const style = window.getComputedStyle(el);
  return style.display === 'none' || style.visibility === 'hidden';
};

const labeledTags = new Set([
  'button',
  'a',
  'input',
  'textarea',
  'select',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
]);

const isLabeledElement = (el: Element): boolean =>
  labeledTags.has(el.tagName.toLowerCase()) ||
  el.getAttribute('role') === 'button' ||
  el.getAttribute('role') === 'link';

const buildAccessibleText = (root: HTMLElement): string => {
  const parts: string[] = [];

  const walker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT, {
    acceptNode: (node) => {
      if (node.nodeType === Node.ELEMENT_NODE) {
        const el = node as Element;
        // Dışlanan, gizli veya aria-hidden alt ağaçları tamamen atla (REJECT = çocuklara da inme)
        if (
          el.matches(EXCLUDE_SELECTOR) ||
          el.getAttribute('aria-hidden') === 'true' ||
          isElementHidden(el)
        ) {
          return NodeFilter.FILTER_REJECT;
        }
        if (isLabeledElement(el)) return NodeFilter.FILTER_ACCEPT;
        return NodeFilter.FILTER_SKIP;
      }

      // TEXT node — etiketli element içindeyse atla (link/buton kendi adıyla okunur)
      const parent = (node as Text).parentElement;
      if (!parent) return NodeFilter.FILTER_SKIP;

      let cursor: Element | null = parent;
      while (cursor && cursor !== root) {
        if (isLabeledElement(cursor)) {
          return NodeFilter.FILTER_SKIP;
        }
        cursor = cursor.parentElement;
      }
      return NodeFilter.FILTER_ACCEPT;
    },
  });

  let current: Node | null = walker.nextNode();
  while (current) {
    if (current.nodeType === Node.TEXT_NODE) {
      const text = (current.textContent || '').replace(/\s+/g, ' ').trim();
      if (text) parts.push(text);
    } else {
      const el = current as Element;
      const name = getElementName(el);
      if (name) {
        const role = getRoleLabel(el);
        const state = getStateLabel(el);
        let phrase = name;
        if (role) phrase += `, ${role}`;
        if (state) phrase += `, ${state}`;
        parts.push(phrase);
      }
    }
    current = walker.nextNode();
  }
  // hızlı okuma gecisini engellemek icin
  return parts.join('... ').replace(/[ \t]+/g, ' ').trim();
};

export function useTextToSpeech() {
  const [isReading, setIsReading] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null);
  const isReadingRef = useRef(false);

  useEffect(() => {
    setIsSupported('speechSynthesis' in window);
    return () => {
      if ('speechSynthesis' in window) {
        speechSynthesis.cancel();
      }
      utteranceRef.current = null;
    };
  }, []);

  // isReading state değiştiğinde ref'i güncelle
  useEffect(() => {
    isReadingRef.current = isReading;
  }, [isReading]);

  // ESC tuşu & başka modal açıldığında sesli okumayı durdur
  useEffect(() => {
    if (!isSupported) return undefined;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isReadingRef.current) {
        e.preventDefault();
        speechSynthesis.cancel();
        setIsReading(false);
        setIsPaused(false);
      }
    };

    const handleMutationAndClick = () => {
      if (isReadingRef.current) {
        speechSynthesis.cancel();
        setIsReading(false);
        setIsPaused(false);
      }
    };

    const handleBackdropClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      
      if (target.classList.contains('MuiBackdrop-root') || 
          (target.getAttribute('role') === 'presentation' && 
           target.getAttribute('aria-hidden') === 'true' &&
           isReadingRef.current)) {
        speechSynthesis.cancel();
        setIsReading(false);
        setIsPaused(false);
      }
    };

    const observer = new MutationObserver(() => {
      // Eğer açık dialog kalmadıysa
      const hasOpenDialog = !!document.querySelector(
        '[role="dialog"][aria-hidden="false"], .MuiDialog-root:not(.hide)'
      );
      
      // Eğer okuma varsa ama dialog kapalıysa durdur
      if (isReadingRef.current && !hasOpenDialog) {
        speechSynthesis.cancel();
        setIsReading(false);
        setIsPaused(false);
      }
    });

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('click', handleBackdropClick, true); // capture phase
    observer.observe(document.body, { 
      childList: true, 
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-hidden', 'class']
    });

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('click', handleBackdropClick, true);
      observer.disconnect();
    };
  }, [isSupported]);

  const speak = (text: string, options: TextToSpeechOptions = {}) => {
    if (!isSupported || !text.trim()) return;
    if (speechSynthesis.speaking || speechSynthesis.paused) {
      speechSynthesis.cancel();
    }
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = options.rate || 1;
    utterance.volume = options.volume !== undefined ? options.volume : 0.8;
    utterance.pitch = options.pitch || 1;
    utterance.lang = options.lang || 'tr-TR';
    utterance.onstart = () => {
      setIsReading(true);
      setIsPaused(false);
    };
    utterance.onend = () => {
      setIsReading(false);
      setIsPaused(false);
    };
    utterance.onerror = () => {
      setIsReading(false);
      setIsPaused(false);
    };
    utteranceRef.current = utterance;
    speechSynthesis.speak(utterance);
    setIsReading(true);
    setIsPaused(false);
  };

  const pause = () => {
    if (!isSupported) return;
    if (speechSynthesis.speaking && !speechSynthesis.paused) {
      speechSynthesis.pause();
      setIsPaused(true);
    }
  };

  const resume = () => {
    if (!isSupported) return;
    if (speechSynthesis.paused) {
      speechSynthesis.resume();
      setIsPaused(false);
    }
  };

  const stop = () => {
    if (!isSupported) return;
    speechSynthesis.cancel();
    utteranceRef.current = null;
    setIsReading(false);
    setIsPaused(false);
  };

  const readPage = (options?: TextToSpeechOptions) => {

    const openDialog =
      document.querySelector<HTMLElement>(
        '[role="dialog"][aria-hidden="false"], [role="alertdialog"][aria-hidden="false"]'
      ) ||
      document.querySelector<HTMLElement>('.MuiDialog-root .MuiDialog-paper');

  
    const target: HTMLElement | null =
      openDialog ||
      document.querySelector<HTMLElement>('#dashboardLayout') ||
      document.querySelector<HTMLElement>('main') ||
      document.querySelector<HTMLElement>('[role="main"]') ||
      document.body;

    if (target) {
      const cleanText = buildAccessibleText(target);
      if (cleanText) speak(cleanText, options);
    }
  };

  const readSelection = (options?: TextToSpeechOptions) => {
    const selection = window.getSelection();
    if (selection && selection.toString().trim()) {
      speak(selection.toString(), options);
    }
  };

  return { speak, pause, resume, stop, readPage, readSelection, isReading, isPaused, isSupported };
}
